import { type HTMLAttributes, useCallback } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useDelayedOpen } from '../../hooks';
import { cn } from '../../utils';
import { DialogOverlay, DialogPortal } from '../Dialog';
import {
  cornerPanelPositionClassNames,
  getCornerPanelAnimationClassName,
  type CornerPanelPosition,
} from './CornerPanel.utils';

export type { CornerPanelPosition } from './CornerPanel.utils';

export interface CornerPanelProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean;
  position?: CornerPanelPosition;
  withBackdrop?: boolean;
  closeOnBackdropClick?: boolean;
  onClose?: () => void;
  backdropProps?: HTMLAttributes<HTMLDivElement>;
  animate?: boolean;
  enterDelayMs?: number;
}

export const CornerPanel = ({
  open,
  position = 'bottom-right',
  withBackdrop = false,
  closeOnBackdropClick = false,
  onClose,
  backdropProps,
  animate = true,
  enterDelayMs = 500,
  children,
  className,
  ...props
}: CornerPanelProps) => {
  const dialogOpen = useDelayedOpen(open, enterDelayMs);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onClose?.();
  };

  const handleInteractOutside = useCallback(
    (event: Event) => event.preventDefault(),
    []
  );

  return (
    <DialogPrimitive.Root
      open={dialogOpen}
      onOpenChange={handleOpenChange}
      modal={withBackdrop}
    >
      <DialogPortal>
        {withBackdrop && (
          <DialogOverlay
            {...backdropProps}
            className={cn(
              'z-60',
              animate && 'corner-panel-backdrop-animation',
              backdropProps?.className
            )}
          />
        )}

        <DialogPrimitive.Content
          className={cn(
            'bg-background fixed z-60 w-full max-w-[320px] rounded-lg border p-6 shadow-lg',
            cornerPanelPositionClassNames[position],
            getCornerPanelAnimationClassName(position, animate),
            className
          )}
          {...(!closeOnBackdropClick && {
            onInteractOutside: handleInteractOutside,
            onPointerDownOutside: handleInteractOutside,
          })}
          onEscapeKeyDown={handleInteractOutside}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
};

CornerPanel.displayName = 'CornerPanel';
